'use client';

import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { useTranslations } from 'next-intl';
import { Numeric } from '@/components/ui/numeric';
import { cn } from '@/lib/utils';
import { NAME_WRAP } from '@/app/[locale]/event/[eventId]/libs/nameBreaks';
import {
  formatGameDuration,
  type ConductLevel,
  type SheetColumn,
  type SheetGame,
  type SheetMarker,
  type SheetSide,
} from '@/app/[locale]/match/libs/scoreSheet';

export interface MatchScoreSheetProps {
  games: SheetGame[];
  /** Two short side labels, one per row of a game. */
  sides: [string, string];
  showServeBox: boolean;
  density: 'page' | 'compact';
  /** The match is being played: the latest cell is marked and the sheet follows it. */
  live?: boolean;
}

// Tailwind v4 generates no CSS for interpolated class names, so each density is written out in
// full. The sideways width is pure CSS: `--sheet-cols` (set inline) times `--sheet-cell-min`
// (set by density and container width) plus the fixed left columns, on a fixed table.
const DENSITY = {
  page: {
    table:
      'min-w-[calc(128px_+_var(--sheet-cols)_*_var(--sheet-cell-min))] [--sheet-cell-min:30px] @sm:[--sheet-cell-min:24px]',
    colLabel: 'w-[56px]',
    colSide: 'w-[36px]',
    colTotal: 'w-[36px]',
    stickySide: 'left-[56px]',
    stickyTotal: 'left-[92px]',
  },
  compact: {
    table:
      'min-w-[calc(104px_+_var(--sheet-cols)_*_var(--sheet-cell-min))] [--sheet-cell-min:22px] @sm:[--sheet-cell-min:16px]',
    colLabel: 'w-[44px]',
    colSide: 'w-[32px]',
    colTotal: 'w-[28px]',
    stickySide: 'left-[44px]',
    stickyTotal: 'left-[76px]',
  },
} as const;

const CELL = 'h-[26px] p-0 text-center align-middle font-mono text-xs whitespace-nowrap';
const MARKERS: SheetMarker[] = ['let', 'noLet', 'stroke'];
const CONDUCT_LEVELS: ConductLevel[] = [
  'warning',
  'conduct_stroke',
  'conduct_game',
  'conduct_match',
];

type Translate = ReturnType<typeof useTranslations<'Scorecard'>>;

const markerLabel = (t: Translate, m: SheetMarker) =>
  m === 'let' ? t('markerLet') : m === 'noLet' ? t('markerNoLet') : t('markerStroke');
const legendWord = (t: Translate, m: SheetMarker) =>
  m === 'let' ? t('legendLet') : m === 'noLet' ? t('legendNoLet') : t('legendStroke');

function conductShort(t: Translate, level: ConductLevel) {
  switch (level) {
    case 'warning':
      return t('conductShortWarning');
    case 'conduct_stroke':
      return t('conductShortStroke');
    case 'conduct_game':
      return t('conductShortGame');
    case 'conduct_match':
      return t('conductShortMatch');
  }
}

function conductFull(t: Translate, level: ConductLevel) {
  switch (level) {
    case 'warning':
      return t('conductWarning');
    case 'conduct_stroke':
      return t('conductStroke');
    case 'conduct_game':
      return t('conductGame');
    case 'conduct_match':
      return t('conductMatch');
  }
}

/** The sheet's one line per game: a total per side, then a column per rally, marker and conduct. */
export function MatchScoreSheet({
  games,
  sides,
  showServeBox,
  density,
  live = false,
}: MatchScoreSheetProps): ReactElement | null {
  const t = useTranslations('Scorecard');
  const d = DENSITY[density];

  const scrollerRef = useRef<HTMLDivElement>(null);
  // Local scroll state only: true while the sheet is at (or being held at) the latest point.
  const followRef = useRef(true);
  const [behind, setBehind] = useState(false);
  const [moreRight, setMoreRight] = useState(false);

  // A live sheet shows only the games that have started.
  const shown = live
    ? games.filter((g) => g.columns.length > 0 || g.score[0] + g.score[1] > 0)
    : games;
  const cols = shown.reduce((n, g) => Math.max(n, g.columns.length), 0);
  const lastGame = shown[shown.length - 1];
  // Changes whenever a point lands on the newest game.
  const latestKey = lastGame ? `${lastGame.number}:${lastGame.columns.length}` : '';

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (el) setMoreRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (live && followRef.current) el.scrollLeft = el.scrollWidth;
    measure();
  }, [live, latestKey, measure]);

  if (shown.length === 0) return null;

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    if (live) {
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
      followRef.current = atEnd;
      setBehind(!atEnd);
    }
    measure();
  };

  const toLatest = () => {
    const el = scrollerRef.current;
    followRef.current = true;
    setBehind(false);
    if (el) el.scrollLeft = el.scrollWidth;
  };

  const present = MARKERS.filter((m) =>
    shown.some((g) => g.columns.some((c) => c.kind === 'marker' && c.marker === m)),
  );
  const presentConduct = CONDUCT_LEVELS.filter((l) =>
    shown.some((g) => g.columns.some((c) => c.kind === 'conduct' && c.level === l)),
  );

  const lastRallyIndex = (g: SheetGame) => {
    for (let k = g.columns.length - 1; k >= 0; k--) if (g.columns[k].kind === 'rally') return k;
    return -1;
  };

  const sideCell = (g: SheetGame, i: SheetSide) => {
    const total = g.score[i];
    const state = g.inProgress ? 'live' : g.winner === i ? 'winner' : 'loser';
    return (
      <Numeric
        data-sheet-total={state}
        className={cn(
          'inline-flex h-5 w-5 items-center justify-center rounded text-xs font-semibold',
          state === 'winner' && 'bg-primary text-primary-foreground',
          state === 'loser' && 'bg-surface text-muted',
          state === 'live' && 'bg-card text-foreground border-border-strong border',
        )}
      >
        {total}
      </Numeric>
    );
  };

  const columnCell = (
    g: SheetGame,
    gi: number,
    col: SheetColumn | undefined,
    k: number,
    i: SheetSide,
    edge: string,
  ) => {
    if (!col) return <td key={k} className={cn(CELL, edge)} />;
    if (col.kind === 'marker') {
      if (i === 1) return null; // spans both rows from the first
      return (
        <td
          key={col.id}
          rowSpan={2}
          data-sheet-marker={col.marker}
          title={legendWord(t, col.marker)}
          className={cn(CELL, edge, 'bg-card text-muted overflow-hidden text-[11px]')}
        >
          {markerLabel(t, col.marker)}
        </td>
      );
    }
    if (col.kind === 'conduct') {
      if (col.side !== i) return <td key={col.id} className={cn(CELL, edge)} />;
      return (
        <td
          key={col.id}
          data-sheet-conduct={col.level}
          title={conductFull(t, col.level)}
          className={cn(
            CELL,
            edge,
            'text-danger-emphasis overflow-hidden text-[11px] font-semibold',
          )}
        >
          {conductShort(t, col.level)}
        </td>
      );
    }
    const cell = col.cells[i];
    const scored = cell.score !== null;
    const box = showServeBox && cell.box !== null;
    if (!scored && !box) return <td key={col.id} className={cn(CELL, edge)} />;
    const isLatest = live && g.inProgress && gi === shown.length - 1 && k === lastRallyIndex(g);
    return (
      <td
        key={col.id}
        data-sheet-cell={scored ? '' : undefined}
        data-sheet-latest={scored && isLatest ? '' : undefined}
        className={cn(
          CELL,
          edge,
          scored && 'bg-surface font-semibold',
          scored && isLatest && 'font-bold shadow-[inset_0_0_0_2px_var(--color-danger-emphasis)]',
        )}
      >
        {scored && <Numeric>{cell.score}</Numeric>}
        {box && (
          <span data-sheet-box className="text-muted ml-px text-[11px] font-normal">
            {cell.box}
          </span>
        )}
      </td>
    );
  };

  return (
    <div className="@container">
      {(density === 'page' || showServeBox) && (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-2">
          {density === 'page' && (
            <h3 className="text-foreground text-sm font-semibold">{t('sheetTitle')}</h3>
          )}
          {showServeBox && <p className="text-muted text-xs">{t('sheetKey')}</p>}
        </div>
      )}

      <ul data-sheet-summary className="sr-only">
        {shown.map((g) => {
          const score = `${g.score[0]}–${g.score[1]}`;
          return (
            <li key={g.number}>
              {g.durationSeconds
                ? t('sheetGameSummaryTimed', {
                    number: g.number,
                    score,
                    duration: formatGameDuration(g.durationSeconds),
                  })
                : t('sheetGameSummary', { number: g.number, score })}
            </li>
          );
        })}
      </ul>

      <div className="border-border relative border-t">
        <div
          ref={scrollerRef}
          data-sheet-scroller
          role="region"
          tabIndex={0}
          aria-label={t('sheetScrollLabel')}
          onScroll={onScroll}
          className="focus-visible:outline-primary overflow-x-auto focus-visible:outline-2 focus-visible:-outline-offset-2"
        >
          <table
            aria-hidden="true"
            style={{ '--sheet-cols': cols } as CSSProperties}
            className={cn('w-full table-fixed border-separate border-spacing-0', d.table)}
          >
            <colgroup>
              <col className={d.colLabel} />
              <col className={d.colSide} />
              <col className={d.colTotal} />
            </colgroup>
            {shown.map((g, gi) => (
              <tbody key={g.number}>
                {([0, 1] as const).map((i) => {
                  const edge =
                    i === 0 && gi > 0 ? 'border-border border-t' : 'border-t border-transparent';
                  return (
                    <tr key={i}>
                      {i === 0 && (
                        <td
                          rowSpan={2}
                          className={cn(
                            CELL,
                            edge,
                            'bg-card sticky left-0 z-[1] pl-3 text-left text-[11px] leading-tight',
                          )}
                        >
                          <Numeric className="text-foreground block text-xs font-semibold">
                            {t('gameShort', { number: g.number })}
                          </Numeric>
                          {g.durationSeconds ? (
                            <Numeric className="text-muted block">
                              {formatGameDuration(g.durationSeconds)}
                            </Numeric>
                          ) : null}
                        </td>
                      )}
                      <td
                        className={cn(
                          CELL,
                          edge,
                          d.stickySide,
                          'bg-card text-muted sticky z-[1] text-left font-sans',
                        )}
                      >
                        <span className={NAME_WRAP}>{sides[i]}</span>
                      </td>
                      <td
                        className={cn(
                          CELL,
                          edge,
                          d.stickyTotal,
                          'bg-card sticky z-[1] shadow-[1px_0_0_var(--color-border)]',
                        )}
                      >
                        {sideCell(g, i)}
                      </td>
                      {Array.from({ length: cols }, (_, k) =>
                        columnCell(g, gi, g.columns[k], k, i, edge),
                      )}
                    </tr>
                  );
                })}
              </tbody>
            ))}
          </table>
        </div>
        <div
          aria-hidden="true"
          data-sheet-fade
          className={cn(
            'to-card pointer-events-none absolute inset-y-0 right-0 w-7 bg-linear-to-r from-transparent transition-opacity',
            moreRight ? 'opacity-100' : 'opacity-0',
          )}
        />
        {live && behind && (
          <button
            type="button"
            onClick={toLatest}
            className="bg-foreground text-background absolute right-2 bottom-1.5 z-[3] rounded-full px-2.5 py-0.5 text-xs font-semibold"
          >
            {t('latest')}
          </button>
        )}
      </div>

      {(present.length > 0 || presentConduct.length > 0) && (
        <p
          data-sheet-legend
          className="text-muted flex flex-wrap gap-x-3 gap-y-0.5 px-3 py-2 text-xs"
        >
          {present.map((m) => (
            <span key={m} data-legend-kind={m}>
              <b className="text-foreground font-mono font-semibold">{markerLabel(t, m)}</b>{' '}
              {legendWord(t, m)}
            </span>
          ))}
          {presentConduct.map((l) => (
            <span key={l} data-legend-kind={l}>
              <b className="text-foreground font-mono font-semibold">{conductShort(t, l)}</b>{' '}
              {conductFull(t, l)}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}
